import { useEffect, useRef, useMemo, useState } from 'react'
import { Loader2, Brain } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface StreamingResponseProps {
  content: string
  isStreaming: boolean
  status?: string
  error?: string | null
  onPreparingResultsChange?: (isPreparing: boolean) => void
}

// Filter out JSON from the content - only show the human-readable part
function getReadableContent(content: string): string {
  if (!content) return ''

  let cleaned = content.trim()

  // NEW: Handle structured output JSON format
  // If the content starts with { it's likely a JSON response with chain_of_thought
  if (cleaned.startsWith('{')) {
    try {
      // Try to parse as JSON and extract chain_of_thought
      const parsed = JSON.parse(cleaned)
      if (parsed.chain_of_thought) {
        return parsed.chain_of_thought
      }
    } catch {
      // If JSON parsing fails, it might be partial/streaming JSON
      // Try to extract chain_of_thought from partial JSON
      // Look for the pattern: "chain_of_thought":"<content>
      const startMarker = '"chain_of_thought":"'
      const startIdx = cleaned.indexOf(startMarker)
      if (startIdx !== -1) {
        // Extract from after the start marker
        const valueStart = startIdx + startMarker.length
        let result = ''
        let i = valueStart

        // Parse the JSON string value, handling escapes
        while (i < cleaned.length) {
          if (cleaned[i] === '\\' && i + 1 < cleaned.length) {
            // Handle escape sequences
            const nextChar = cleaned[i + 1]
            if (nextChar === 'n') result += '\n'
            else if (nextChar === 't') result += '\t'
            else if (nextChar === '"') result += '"'
            else if (nextChar === '\\') result += '\\'
            else result += cleaned[i + 1]
            i += 2
          } else if (cleaned[i] === '"') {
            // End of string value
            break
          } else {
            result += cleaned[i]
            i++
          }
        }

        if (result) {
          return result
        }
      }
      // If no chain_of_thought found, show nothing (the JSON structure is incomplete)
      return ''
    }
  }

  // LEGACY: Handle old format with PART 1/PART 2 headers
  cleaned = cleaned
    .replace(/\*\*PART 1[^*]*\*\*/gi, '')
    .replace(/\*\*PART 2[^*]*\*\*/gi, '')
    .replace(/^PART 1[^\n]*\n/gim, '')
    .replace(/^PART 2[^\n]*\n/gim, '')
    .trim()

  // Look for the ---JSON--- separator and cut everything after
  const jsonSeparator = '---JSON---'
  const separatorIndex = cleaned.indexOf(jsonSeparator)
  if (separatorIndex !== -1) {
    cleaned = cleaned.substring(0, separatorIndex).trim()
  }

  // Handle partial separators being typed
  const partialSeparators = ['---J', '---JS', '---JSO', '---JSON', '---JSON-', '---JSON--']
  for (const partial of partialSeparators) {
    if (cleaned.endsWith(partial)) {
      cleaned = cleaned.substring(0, cleaned.length - partial.length).trim()
    }
  }

  // Remove any trailing JSON objects that might slip through
  const lastBraceOpen = cleaned.lastIndexOf('\n{')
  if (lastBraceOpen !== -1) {
    const afterBrace = cleaned.substring(lastBraceOpen)
    // If it looks like JSON (has quotes and colons), remove it
    if (afterBrace.includes('"') && afterBrace.includes(':')) {
      cleaned = cleaned.substring(0, lastBraceOpen).trim()
    }
  }

  return cleaned
}

// Map status codes to human-readable messages
function getStatusMessage(status?: string): string {
  const statusMap: Record<string, string> = {
    'gathering_context': 'Gathering pond data...',
    'preparing_analysis': 'Preparing analysis...',
    'analyzing': 'Analyzing...',
    'analyzing_image': 'Examining image...',
    'processing': 'Processing...',
    'finalizing': 'Finalizing...',
  }
  return statusMap[status || ''] || status || 'Thinking...'
}

export function StreamingResponse({
  content,
  isStreaming,
  status,
  error,
  onPreparingResultsChange,
}: StreamingResponseProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const lastContentRef = useRef<string>('')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isPreparingResults, setIsPreparingResults] = useState(false)

  // Get only the readable content (filter out JSON)
  const readableContent = useMemo(() => getReadableContent(content), [content])

  // Handle content changes and streaming state via effect
  // All setState calls are inside setTimeout callbacks to satisfy react-hooks/set-state-in-effect
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }

    // If not streaming, schedule state reset
    if (!isStreaming) {
      timerRef.current = setTimeout(() => {
        setIsPreparingResults(false)
      }, 0)
      lastContentRef.current = ''
      return
    }

    // If content changed, reset preparing state and start new timer
    if (readableContent !== lastContentRef.current) {
      lastContentRef.current = readableContent

      // Schedule immediate reset, then schedule preparing state after delay
      timerRef.current = setTimeout(() => {
        setIsPreparingResults(false)

        // If we have substantial content, set timer to detect stall
        if (readableContent.length > 100) {
          timerRef.current = setTimeout(() => {
            setIsPreparingResults(true)
          }, 1500)
        }
      }, 0)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [readableContent, isStreaming])

  // Notify parent of preparing results state change
  useEffect(() => {
    onPreparingResultsChange?.(isPreparingResults)
  }, [isPreparingResults, onPreparingResultsChange])

  // Auto-scroll to bottom as content streams
  useEffect(() => {
    if (containerRef.current && isStreaming) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [readableContent, isStreaming])

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5">
        <p className="text-sm font-medium text-red-800">Analysis Error</p>
        <p className="mt-1 text-sm text-red-600">{error}</p>
      </div>
    )
  }

  if (!content && !isStreaming) {
    return null
  }

  return (
    <div className="rounded-xl border border-slate-200/50 bg-white shadow-sm overflow-hidden">
      {/* Status Header */}
      {isStreaming && (
        <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50/30 px-4 py-3">
          <Brain className="h-5 w-5 text-emerald-600" />
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              {getStatusMessage(status)}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div
        ref={containerRef}
        className="max-h-[500px] overflow-y-auto p-5"
      >
        {/* Show loading if no content yet */}
        {isStreaming && !readableContent && (
          <div className="flex items-center justify-center py-6">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
              <p className="mt-3 text-sm text-slate-500">Starting analysis...</p>
            </div>
          </div>
        )}

        {/* Stream the readable content with markdown rendering */}
        {readableContent && (
          <div className="prose prose-sm prose-slate max-w-none prose-p:my-2 prose-headings:mt-4 prose-headings:mb-2 prose-ul:my-2 prose-li:my-0.5 prose-strong:text-slate-800">
            <ReactMarkdown>{readableContent}</ReactMarkdown>
            {/* Streaming cursor - hide when preparing results */}
            {isStreaming && !isPreparingResults && (
              <span className="inline-block h-4 w-1.5 animate-pulse rounded bg-emerald-500 ml-0.5" />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default StreamingResponse
